const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper para manejar respuestas
const handleResponse = async (response) => {
    // 304 Not Modified: body vacio. Sirve como cache-hit — retornamos
    // marcador vacio para que el caller siga sin explotar en .json().
    if (response.status === 304) {
        return { success: true, data: [], _notModified: true };
    }
    let data;
    try {
        data = await response.json();
    } catch (parseErr) {
        console.error(`[api] JSON parse fail — status ${response.status} ${response.statusText} ${response.url}`, parseErr);
        throw new Error(`Respuesta invalida del servidor (${response.status})`);
    }
    if (!response.ok) {
        // 401: token invalidado (rol cambiado, cuenta desactivada, forzar logout).
        // Disparamos un evento global para que AuthContext limpie sesion local y
        // redirija al login. No repetimos si estamos ya en /login para evitar loops.
        if (response.status === 401 && !String(response.url).includes('/auth/login')) {
            try {
                window.dispatchEvent(new CustomEvent('auth:session-expired', {
                    detail: { message: data?.message || 'Sesión expirada' }
                }));
            } catch (_) { /* noop */ }
        }
        // 403/404 son flujos esperados (sin permiso / recurso inexistente): warn.
        // El resto (500 en particular) se loguea como error para investigar.
        const logFn = (response.status === 403 || response.status === 404) ? console.warn : console.error;
        logFn(`[api] ${response.status} ${response.statusText} ${response.url}`, data?.message || '');
        // Adjuntar status al Error para que los callers puedan discriminar
        // 404 (recurso inexistente) vs 403 (sin acceso) vs 500 (server error).
        const err = new Error(data.message || `Error ${response.status}`);
        err.status = response.status;
        err.body = data;
        throw err;
    }
    return data;
};

// Helper para obtener headers con token
const getHeaders = () => {
    // Buscar token en ambos storages (localStorage para "recordarme", sessionStorage para sesión normal)
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

// ============ AUTH ============
export const authAPI = {
    login: async (email, password, rememberMe = false) => {
        const response = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, rememberMe })
        });
        return handleResponse(response);
    },

    getProfile: async () => {
        const response = await fetch(`${API_URL}/auth/profile`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    // updateProfile acepta: string (legacy: solo nombre) o { nombre?, slack_member_id? }
    updateProfile: async (payload) => {
        const body = typeof payload === 'string' ? { nombre: payload } : payload;
        const response = await fetch(`${API_URL}/auth/profile`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(body)
        });
        return handleResponse(response);
    },

    changePassword: async (newPassword) => {
        const response = await fetch(`${API_URL}/auth/change-password`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ newPassword })
        });
        return handleResponse(response);
    },

    forgotPassword: async (email) => {
        const response = await fetch(`${API_URL}/auth/forgot-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        });
        return handleResponse(response);
    },

    resetPassword: async (token, password) => {
        const response = await fetch(`${API_URL}/auth/reset-password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, password })
        });
        return handleResponse(response);
    },

    validateResetToken: async (token) => {
        const response = await fetch(`${API_URL}/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
        return handleResponse(response);
    }
};

// ============ USUARIOS ============
export const usuariosAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/usuarios`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/usuarios/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/usuarios`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/usuarios/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    resetPassword: async (id, newPassword) => {
        const response = await fetch(`${API_URL}/usuarios/${id}/reset-password`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ newPassword })
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/usuarios/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    // Fuerza cierre de sesion del usuario. Solo superadmin puede ejecutarlo.
    forceLogout: async (id) => {
        const response = await fetch(`${API_URL}/usuarios/${id}/force-logout`, {
            method: 'POST',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ CATEGORÍAS DE SERVICIO ============
export const categoriasAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/categorias?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/categorias/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/categorias`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/categorias/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/categorias/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ TIPOS DE SERVICIO ============
export const tiposServicioAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/tipos-servicio?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getGrouped: async () => {
        const response = await fetch(`${API_URL}/tipos-servicio/grouped`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/tipos-servicio/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/tipos-servicio`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/tipos-servicio/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/tipos-servicio/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ PERSONAL ============
export const personalAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/personal?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/personal/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/personal`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/personal/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/personal/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ CATEGORÍAS DE COSTO FIJO ============
export const categoriasCostoFijoAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/categorias-costo-fijo?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/categorias-costo-fijo/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/categorias-costo-fijo`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/categorias-costo-fijo/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/categorias-costo-fijo/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ COSTOS FIJOS ============
export const costosFijosAPI = {
    getAll: async (includeInactive = false) => {
        const response = await fetch(
            `${API_URL}/costos-fijos?includeInactive=${includeInactive}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getGrouped: async () => {
        const response = await fetch(`${API_URL}/costos-fijos/grouped`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/costos-fijos/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/costos-fijos`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/costos-fijos/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/costos-fijos/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getPrecios: async (id) => {
        const response = await fetch(`${API_URL}/costos-fijos/${id}/precios`, {
            headers: getHeaders(),
            cache: 'no-store'
        });
        return handleResponse(response);
    },

    savePrecios: async (id, precios) => {
        const response = await fetch(`${API_URL}/costos-fijos/${id}/precios`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ precios })
        });
        return handleResponse(response);
    }
};

// ============ CONFIGURACION ============
export const configuracionAPI = {
    // Endpoint público (sin autenticación) - para Login
    getPublicLogo: async () => {
        const response = await fetch(`${API_URL}/configuracion/public/logo`);
        return handleResponse(response);
    },

    getAll: async () => {
        const response = await fetch(`${API_URL}/configuracion`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getByKey: async (clave) => {
        const response = await fetch(`${API_URL}/configuracion/${clave}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    update: async (clave, valor) => {
        const response = await fetch(`${API_URL}/configuracion/${clave}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify({ valor })
        });
        return handleResponse(response);
    },

    updateMultiple: async (configs) => {
        const response = await fetch(`${API_URL}/configuracion/batch`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(configs)
        });
        return handleResponse(response);
    },

    getTipoCambioSunat: async () => {
        const response = await fetch(`${API_URL}/configuracion/tipo-cambio/sunat`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getTipoCambioAPI: async () => {
        const response = await fetch(`${API_URL}/configuracion/tipo-cambio/api`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getNextCotizacionNumber: async (prefijoId = null) => {
        const qs = prefijoId ? `?prefijo_id=${encodeURIComponent(prefijoId)}` : '';
        const response = await fetch(`${API_URL}/configuracion/next-cotizacion-number${qs}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ COTIZACIONES ============
export const cotizacionesAPI = {
    getAll: async (params = {}) => {
        const queryParams = new URLSearchParams(params).toString();
        const response = await fetch(
            `${API_URL}/cotizaciones${queryParams ? `?${queryParams}` : ''}`,
            { headers: getHeaders() }
        );
        return handleResponse(response);
    },

    getMyCotizaciones: async () => {
        const response = await fetch(`${API_URL}/cotizaciones/mis-cotizaciones`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getEstadoHistorial: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/estado-historial`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getHilo: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/hilo`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    marcarPrincipal: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/marcar-principal`, {
            method: 'PATCH',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    // ============ LOCKS DE EDICION ============
    getLock: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/lock`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    tomarLock: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/lock`, {
            method: 'POST',
            headers: getHeaders()
        });
        // Caso 409: lock tomado por otro. Devolvemos sin throw para que el
        // caller pueda inspeccionar el detalle del owner.
        if (response.status === 409) {
            const body = await response.json().catch(() => ({}));
            return { success: false, status: 409, ...body };
        }
        return handleResponse(response);
    },

    liberarLock: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/lock`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    solicitarLock: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/lock/solicitar`, {
            method: 'POST',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    rechazarSolicitud: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/lock/solicitud`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    forzarLock: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/lock/forzar`, {
            method: 'POST',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getBorrador: async () => {
        const response = await fetch(`${API_URL}/cotizaciones/borrador`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    upsertBorrador: async (payload) => {
        const response = await fetch(`${API_URL}/cotizaciones/borrador`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ payload })
        });
        return handleResponse(response);
    },

    deleteBorrador: async () => {
        const response = await fetch(`${API_URL}/cotizaciones/borrador`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/cotizaciones`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    updateEstado: async (id, estado) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/estado`, {
            method: 'PATCH',
            headers: getHeaders(),
            body: JSON.stringify({ estado })
        });
        return handleResponse(response);
    },

    duplicate: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/duplicate`, {
            method: 'POST',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    createVersion: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/version`, {
            method: 'POST',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    // Archivo adjunto (solo cotizaciones manuales).
    subirArchivo: async (id, file) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const formData = new FormData();
        formData.append('archivo', file);
        const response = await fetch(`${API_URL}/cotizaciones/${id}/archivo`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}, // sin Content-Type: multipart lo pone el browser
            body: formData
        });
        return handleResponse(response);
    },
    // URL directa para <a href> — el browser sigue la sesion via cookie? no, usamos JWT.
    // Como alternativa: fetch con auth, obtener blob y crear objectURL.
    descargarArchivo: async (id) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await fetch(`${API_URL}/cotizaciones/${id}/archivo?download=1`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!response.ok) throw new Error('No se pudo descargar el archivo');
        const blob = await response.blob();
        const disp = response.headers.get('Content-Disposition') || '';
        const match = disp.match(/filename="?([^";]+)"?/);
        const fname = match ? match[1] : 'archivo';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    verArchivo: async (id) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await fetch(`${API_URL}/cotizaciones/${id}/archivo`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!response.ok) throw new Error('No se pudo abrir el archivo');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    eliminarArchivo: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/archivo`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    // Anexos globales (multiples archivos por hilo de cotizacion).
    listarAnexos: async (id) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/anexos`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },
    subirAnexos: async (id, files) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const formData = new FormData();
        (Array.isArray(files) ? files : Array.from(files || [])).forEach(f => {
            formData.append('archivos', f);
        });
        const response = await fetch(`${API_URL}/cotizaciones/${id}/anexos`, {
            method: 'POST',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            body: formData
        });
        return handleResponse(response);
    },
    verAnexo: async (id, anexoId) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await fetch(`${API_URL}/cotizaciones/${id}/anexos/${anexoId}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!response.ok) throw new Error('No se pudo abrir el anexo');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    descargarAnexo: async (id, anexoId, fallbackName) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const response = await fetch(`${API_URL}/cotizaciones/${id}/anexos/${anexoId}?download=1`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (!response.ok) throw new Error('No se pudo descargar el anexo');
        const blob = await response.blob();
        const disp = response.headers.get('Content-Disposition') || '';
        const match = disp.match(/filename="?([^";]+)"?/);
        const fname = match ? match[1] : (fallbackName || 'anexo');
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    eliminarAnexo: async (id, anexoId) => {
        const response = await fetch(`${API_URL}/cotizaciones/${id}/anexos/${anexoId}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// API de Cobranzas: pagos parciales sobre cotizaciones aprobadas.
// Las rutas viven bajo /cotizaciones/:id/pagos para mantener namespace.
export const cobranzasAPI = {
    getPagos: async (cotizacionId) => {
        const response = await fetch(`${API_URL}/cotizaciones/${cotizacionId}/pagos`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    registrarPago: async (cotizacionId, data) => {
        const response = await fetch(`${API_URL}/cotizaciones/${cotizacionId}/pagos`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    editarPago: async (cotizacionId, pagoId, data) => {
        const response = await fetch(`${API_URL}/cotizaciones/${cotizacionId}/pagos/${pagoId}`, {
            method: 'PATCH',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    anularPago: async (cotizacionId, pagoId, motivo) => {
        const response = await fetch(`${API_URL}/cotizaciones/${cotizacionId}/pagos/${pagoId}/anular`, {
            method: 'PATCH',
            headers: getHeaders(),
            body: JSON.stringify({ motivo })
        });
        return handleResponse(response);
    }
};

// API de Plantillas
export const plantillasAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/plantillas`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/plantillas/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/plantillas`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/plantillas/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/plantillas/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    // ===== Categorías de plantillas (CRUD) =====
    getCategorias: async () => {
        const response = await fetch(`${API_URL}/plantillas/categorias`, { headers: getHeaders() });
        return handleResponse(response);
    },
    createCategoria: async (data) => {
        const response = await fetch(`${API_URL}/plantillas/categorias`, {
            method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    updateCategoria: async (id, data) => {
        const response = await fetch(`${API_URL}/plantillas/categorias/${id}`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    deleteCategoria: async (id) => {
        const response = await fetch(`${API_URL}/plantillas/categorias/${id}`, {
            method: 'DELETE', headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// API de Clientes
export const clientesAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/clientes`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getWithStats: async (params = {}) => {
        // Filter out undefined values before creating URL params
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
        );
        const queryParams = new URLSearchParams(filteredParams).toString();
        const response = await fetch(`${API_URL}/clientes/with-stats${queryParams ? `?${queryParams}` : ''}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    search: async (query) => {
        const response = await fetch(`${API_URL}/clientes/search?q=${encodeURIComponent(query || '')}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getById: async (id) => {
        const response = await fetch(`${API_URL}/clientes/${id}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    getCotizaciones: async (id, estado = null) => {
        const params = estado ? `?estado=${estado}` : '';
        const response = await fetch(`${API_URL}/clientes/${id}/cotizaciones${params}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    create: async (data) => {
        const response = await fetch(`${API_URL}/clientes`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    update: async (id, data) => {
        const response = await fetch(`${API_URL}/clientes/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    delete: async (id) => {
        const response = await fetch(`${API_URL}/clientes/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    // ===== Contactos del cliente =====
    getContactos: async (clienteId, params = {}) => {
        const filtered = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
        );
        const qs = new URLSearchParams(filtered).toString();
        const response = await fetch(`${API_URL}/clientes/${clienteId}/contactos${qs ? `?${qs}` : ''}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },

    createContacto: async (clienteId, data) => {
        const response = await fetch(`${API_URL}/clientes/${clienteId}/contactos`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    updateContacto: async (clienteId, contactoId, data) => {
        const response = await fetch(`${API_URL}/clientes/${clienteId}/contactos/${contactoId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    deleteContacto: async (clienteId, contactoId) => {
        const response = await fetch(`${API_URL}/clientes/${clienteId}/contactos/${contactoId}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ UPLOAD ============
export const uploadAPI = {
    uploadLogo: async (file) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const formData = new FormData();
        formData.append('logo', file);

        const response = await fetch(`${API_URL}/upload/logo`, {
            method: 'POST',
            headers: {
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            body: formData
        });
        const result = await handleResponse(response);
        // Asegurar que solo se guarde la ruta relativa
        if (result.url && result.url.startsWith('http')) {
            try {
                result.url = new URL(result.url).pathname;
            } catch (e) { /* mantener url original */ }
        }
        return result;
    },

    deleteFile: async (filename) => {
        const response = await fetch(`${API_URL}/upload/${filename}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ DASHBOARD ============
export const dashboardAPI = {
    getStats: async (params = {}) => {
        const filteredParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
        );
        const queryParams = new URLSearchParams(filteredParams).toString();
        const response = await fetch(`${API_URL}/dashboard/stats${queryParams ? `?${queryParams}` : ''}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ ALERTAS ============
export const alertasAPI = {
    // Tipos de actividad
    getTiposActividad: async () => {
        const response = await fetch(`${API_URL}/alertas/tipos-actividad`, { headers: getHeaders() });
        return handleResponse(response);
    },
    createTipoActividad: async (data) => {
        const response = await fetch(`${API_URL}/alertas/tipos-actividad`, {
            method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    updateTipoActividad: async (id, data) => {
        const response = await fetch(`${API_URL}/alertas/tipos-actividad/${id}`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    deleteTipoActividad: async (id) => {
        const response = await fetch(`${API_URL}/alertas/tipos-actividad/${id}`, {
            method: 'DELETE', headers: getHeaders()
        });
        return handleResponse(response);
    },

    // Config alertas (umbrales y colores)
    getConfig: async () => {
        const response = await fetch(`${API_URL}/alertas/config`, { headers: getHeaders() });
        return handleResponse(response);
    },
    updateConfig: async (id, data) => {
        const response = await fetch(`${API_URL}/alertas/config/${id}`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    // SMTP
    getSmtp: async () => {
        const response = await fetch(`${API_URL}/alertas/smtp`, { headers: getHeaders() });
        return handleResponse(response);
    },
    updateSmtp: async (data) => {
        const response = await fetch(`${API_URL}/alertas/smtp`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    testSmtp: async (email) => {
        const response = await fetch(`${API_URL}/alertas/smtp/test`, {
            method: 'POST', headers: getHeaders(), body: JSON.stringify({ email })
        });
        return handleResponse(response);
    },

    // Email templates
    getTemplates: async () => {
        const response = await fetch(`${API_URL}/alertas/templates`, { headers: getHeaders() });
        return handleResponse(response);
    },
    updateTemplate: async (id, data) => {
        const response = await fetch(`${API_URL}/alertas/templates/${id}`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },

    // Slack
    getSlack: async () => {
        const response = await fetch(`${API_URL}/alertas/slack`, { headers: getHeaders() });
        return handleResponse(response);
    },
    updateSlack: async (data) => {
        const response = await fetch(`${API_URL}/alertas/slack`, {
            method: 'PUT', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    testSlack: async (mentionId = null) => {
        const response = await fetch(`${API_URL}/alertas/slack/test`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ mentionId: mentionId || null })
        });
        return handleResponse(response);
    },

    // Actividades de cotización
    getActividades: async (cotizacionId) => {
        const response = await fetch(`${API_URL}/alertas/cotizacion/${cotizacionId}/actividades`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },
    registrarActividad: async (cotizacionId, data) => {
        const response = await fetch(`${API_URL}/alertas/cotizacion/${cotizacionId}/actividades`, {
            method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
        });
        return handleResponse(response);
    }
};

export const permisosAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/permisos`, { headers: getHeaders() });
        return handleResponse(response);
    },
    update: async (rol, permisos) => {
        const response = await fetch(`${API_URL}/permisos`, {
            method: 'PUT', headers: getHeaders(),
            body: JSON.stringify({ rol, permisos })
        });
        return handleResponse(response);
    },
};

export const aiAPI = {
    // Estado del sistema IA (circuit breaker + switch global).
    // Devuelve { ia_activada, any_available, providers: { gemini: {...}, deepseek: {...} } }
    getStatus: async () => {
        const response = await fetch(`${API_URL}/ai/status`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },
    reactivarProvider: async (provider) => {
        const response = await fetch(`${API_URL}/ai/reactivar-provider`, {
            method: 'POST', headers: getHeaders(),
            body: JSON.stringify({ provider })
        });
        return handleResponse(response);
    },
    testProvider: async (provider) => {
        const response = await fetch(`${API_URL}/ai/test-provider`, {
            method: 'POST', headers: getHeaders(),
            body: JSON.stringify({ provider })
        });
        return handleResponse(response);
    },
    generarCotizacion: async (prompt) => {
        const response = await fetch(`${API_URL}/ai/generar-cotizacion`, {
            method: 'POST', headers: getHeaders(),
            body: JSON.stringify({ prompt })
        });
        return handleResponse(response);
    },
    chatCotizacion: async (messages) => {
        const response = await fetch(`${API_URL}/ai/chat-cotizacion`, {
            method: 'POST', headers: getHeaders(),
            body: JSON.stringify({ messages })
        });
        return handleResponse(response);
    },
    ejecutarSugerencias: async () => {
        const response = await fetch(`${API_URL}/ai/ejecutar-sugerencias`, {
            method: 'POST', headers: getHeaders()
        });
        return handleResponse(response);
    },
    obtenerAnalisisDashboard: async () => {
        const response = await fetch(`${API_URL}/ai/analisis-dashboard`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },
    regenerarAnalisisDashboard: async () => {
        const response = await fetch(`${API_URL}/ai/analisis-dashboard/regenerar`, {
            method: 'POST', headers: getHeaders()
        });
        return handleResponse(response);
    },
    obtenerMetricas: async ({ desde, hasta } = {}) => {
        const params = new URLSearchParams();
        if (desde) params.append('desde', desde);
        if (hasta) params.append('hasta', hasta);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const response = await fetch(`${API_URL}/ai/metricas${qs}`, {
            headers: getHeaders()
        });
        return handleResponse(response);
    },
};

// ============ PERIODOS DE SERVICIO ============
export const periodosServicioAPI = {
    getAll: async (soloActivos = false) => {
        const qs = soloActivos ? '?activo=1' : '';
        const response = await fetch(`${API_URL}/servicio-periodos${qs}`, {
            headers: getHeaders(),
            cache: 'no-store'
        });
        return handleResponse(response);
    },
    create: async (data) => {
        const response = await fetch(`${API_URL}/servicio-periodos`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    update: async (id, data) => {
        const response = await fetch(`${API_URL}/servicio-periodos/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    delete: async (id) => {
        const response = await fetch(`${API_URL}/servicio-periodos/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// ============ PLANES DE PAGO ============
export const planesPagoAPI = {
    getAll: async (soloActivos = false) => {
        const qs = soloActivos ? '?activo=1' : '';
        const response = await fetch(`${API_URL}/planes-pago${qs}`, {
            headers: getHeaders(),
            cache: 'no-store'
        });
        return handleResponse(response);
    },
    getById: async (id) => {
        const response = await fetch(`${API_URL}/planes-pago/${id}`, { headers: getHeaders() });
        return handleResponse(response);
    },
    create: async (data) => {
        const response = await fetch(`${API_URL}/planes-pago`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    update: async (id, data) => {
        const response = await fetch(`${API_URL}/planes-pago/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    delete: async (id) => {
        const response = await fetch(`${API_URL}/planes-pago/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

export const notificacionesAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/notificaciones`, { headers: getHeaders() });
        return handleResponse(response);
    },
    marcarLeida: async (id) => {
        const response = await fetch(`${API_URL}/notificaciones/${id}/leida`, {
            method: 'PATCH', headers: getHeaders()
        });
        return handleResponse(response);
    },
    marcarTodasLeidas: async () => {
        const response = await fetch(`${API_URL}/notificaciones/marcar-todas-leidas`, {
            method: 'PATCH', headers: getHeaders()
        });
        return handleResponse(response);
    },
    eliminar: async (id) => {
        const response = await fetch(`${API_URL}/notificaciones/${id}`, {
            method: 'DELETE', headers: getHeaders()
        });
        return handleResponse(response);
    },
    // Historial completo paginado. estado: 'todas' | 'leidas' | 'no_leidas'
    getHistorial: async ({ estado = 'todas', offset = 0, limit = 50 } = {}) => {
        const qs = new URLSearchParams({ estado, offset: String(offset), limit: String(limit) });
        const response = await fetch(`${API_URL}/notificaciones/historial?${qs}`, { headers: getHeaders() });
        return handleResponse(response);
    },
    eliminarLeidas: async () => {
        const response = await fetch(`${API_URL}/notificaciones/leidas`, {
            method: 'DELETE', headers: getHeaders()
        });
        return handleResponse(response);
    }
};

// API de logs del sistema
export const logsAPI = {
    list: async (params = {}) => {
        const qs = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') qs.append(k, v);
        });
        const response = await fetch(`${API_URL}/logs?${qs.toString()}`, { headers: getHeaders() });
        return handleResponse(response);
    },
    stats: async () => {
        const response = await fetch(`${API_URL}/logs/stats`, { headers: getHeaders() });
        return handleResponse(response);
    },
    // body: { ids?, level?, category?, before? }
    bulkDelete: async (body) => {
        const response = await fetch(`${API_URL}/logs`, {
            method: 'DELETE',
            headers: getHeaders(),
            body: JSON.stringify(body)
        });
        return handleResponse(response);
    },
    deleteOne: async (id) => {
        const response = await fetch(`${API_URL}/logs/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    },
    // Reporta un error/warning del navegador al backend. No usa handleResponse
    // — nunca debe throwear (silenciamos fallos de red para no cascadear).
    reportFrontend: async (payload) => {
        try {
            await fetch(`${API_URL}/logs/frontend`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(payload || {})
            });
        } catch (_) { /* noop */ }
    }
};

// API de prefijos de cotizacion (numeraciones)
export const prefijosAPI = {
    getAll: async () => {
        const response = await fetch(`${API_URL}/prefijos`, { headers: getHeaders() });
        return handleResponse(response);
    },
    getActivos: async (tipo = null) => {
        const qs = tipo ? `?tipo=${encodeURIComponent(tipo)}` : '';
        // cache:'no-store' evita 304 sin body (el backend devuelve ETag y el
        // browser envia If-None-Match automaticamente, dejando fetch sin JSON).
        const response = await fetch(`${API_URL}/prefijos/activos${qs}`, {
            headers: getHeaders(),
            cache: 'no-store'
        });
        return handleResponse(response);
    },
    create: async (data) => {
        const response = await fetch(`${API_URL}/prefijos`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    update: async (id, data) => {
        const response = await fetch(`${API_URL}/prefijos/${id}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        return handleResponse(response);
    },
    setDefault: async (id) => {
        const response = await fetch(`${API_URL}/prefijos/${id}/default`, {
            method: 'PATCH',
            headers: getHeaders()
        });
        return handleResponse(response);
    },
    toggleActivo: async (id) => {
        const response = await fetch(`${API_URL}/prefijos/${id}/activo`, {
            method: 'PATCH',
            headers: getHeaders()
        });
        return handleResponse(response);
    },
    delete: async (id) => {
        const response = await fetch(`${API_URL}/prefijos/${id}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        return handleResponse(response);
    }
};

const api = {
    auth: authAPI,
    usuarios: usuariosAPI,
    categorias: categoriasAPI,
    tiposServicio: tiposServicioAPI,
    personal: personalAPI,
    categoriasCostoFijo: categoriasCostoFijoAPI,
    costosFijos: costosFijosAPI,
    configuracion: configuracionAPI,
    cotizaciones: cotizacionesAPI,
    plantillas: plantillasAPI,
    clientes: clientesAPI,
    upload: uploadAPI,
    dashboard: dashboardAPI,
    alertas: alertasAPI,
    permisos: permisosAPI,
    prefijos: prefijosAPI,
    periodosServicio: periodosServicioAPI
};

export default api;
